import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from '../../services/auth.service';
import {RoleService} from '../../services/role.service';
import {
    CameraAnalyticsConfigState,
    CameraAnalyticsConfigViewService,
    CameraAnalyticsRow,
} from '../../services/vte/camera-analytics-config-view.service';
import {CameraFilterOption} from '../../services/vte/operations-filters.service';
import {OrganizationOption} from '../../services/vte/operations-organization.service';
import {
    customerAnalyticsDescription,
    customerAnalyticsName,
    customerConfigurationSourceLabel,
    formatProviderLabel,
} from '../../services/vte/vision-customer-labels';

@Component({
    selector: 'app-camera-analytics-config',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './camera-analytics-config.component.html',
    styleUrl: './camera-analytics-config.component.css',
})
export class CameraAnalyticsConfigComponent implements OnInit {
    loadingCameras = false;
    loadingConfig = false;
    saving = false;
    selectedOrgId = '';
    selectedCameraId = '';
    organizations: OrganizationOption[] = [];
    cameras: CameraFilterOption[] = [];
    configState: CameraAnalyticsConfigState | null = null;
    correlationWindowSeconds = 30;
    private savedSnapshot = '';

    constructor(
        public viewService: CameraAnalyticsConfigViewService,
        private auth: AuthService,
        private roleService: RoleService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        if (this.viewService.canFilterByOrganization()) {
            this.viewService.loadOrganizationOptions().subscribe({
                next: (organizations) => {
                    this.organizations = organizations;
                },
            });
        }
        this.loadCameras();
    }

    get canFilterByOrganization(): boolean {
        return this.viewService.canFilterByOrganization();
    }

    get rows(): CameraAnalyticsRow[] {
        const rows = this.configState?.rows || [];
        // Always list both vehicle-detection providers; exclusivity is enforced on toggle/save.
        return this.viewService.groupRowsForCustomer(rows);
    }

    get canEdit(): boolean {
        const groups = this.auth.getCurrentUserInfo()?.role;
        return this.roleService.canEditVisionConfiguration(groups);
    }

    get showImplementationDetails(): boolean {
        return this.auth.isSuperUser();
    }

    /** Explicit CameraAnalyticsConfiguration rows exist for this camera. */
    get hasCameraConfiguration(): boolean {
        return this.viewService.hasExplicitConfiguration(this.configState?.configurationSource);
    }

    get analyticsReadOnly(): boolean {
        return !this.canEdit || !this.eenOauthConfigured;
    }

    get canClearConfiguration(): boolean {
        return (
            this.canEdit
            && this.hasCameraConfiguration
            && !this.saving
            && !this.hasUnsavedChanges
            && this.eenOauthConfigured
        );
    }

    get hasUnsavedChanges(): boolean {
        return this.serializeState() !== this.savedSnapshot;
    }

    /** Platform EEN OAuth credential must exist before Camera Analytics writes. */
    get eenOauthConfigured(): boolean {
        return !!this.configState?.eenOauthConfigured;
    }

    get analyticsReadOnlyDueToAuth(): boolean {
        return this.canEdit && !this.eenOauthConfigured;
    }

    get siblingFailureSummary(): string {
        const failures = this.configState?.subscriptionProvisioning?.sibling_failures || [];
        return failures
            .map((failure) => failure.camera_esn)
            .filter((esn) => !!esn)
            .join(', ');
    }

    customerAnalyticsName = customerAnalyticsName;
    customerAnalyticsDescription = customerAnalyticsDescription;
    customerConfigurationSourceLabel = customerConfigurationSourceLabel;

    get enabledAnalyticsCount(): number {
        return this.rows.filter((row) => row.isEnabled).length;
    }

    get showCorrelationSettings(): boolean {
        return this.enabledAnalyticsCount > 1;
    }

    providerLabel(providerId: string | null | undefined): string {
        return formatProviderLabel(providerId, this.showImplementationDetails);
    }

    onOrganizationChange(): void {
        this.selectedCameraId = '';
        this.configState = null;
        this.loadCameras();
    }

    onCameraChange(): void {
        this.loadConfiguration();
    }

    loadCameras(): void {
        const orgId = this.viewService.resolveOrganizationId(this.selectedOrgId) ?? undefined;
        this.loadingCameras = true;
        this.viewService.loadCameraOptions(orgId).subscribe({
            next: (cameras) => {
                this.cameras = cameras;
                this.loadingCameras = false;
                if (this.selectedCameraId && !cameras.some((camera) => String(camera.id) === this.selectedCameraId)) {
                    this.selectedCameraId = '';
                    this.configState = null;
                }
            },
            error: () => {
                this.cameras = [];
                this.loadingCameras = false;
            },
        });
    }

    loadConfiguration(): void {
        const cameraId = Number(this.selectedCameraId);
        if (!cameraId || Number.isNaN(cameraId)) {
            this.configState = null;
            return;
        }

        this.loadingConfig = true;
        this.viewService.loadConfiguration(cameraId).subscribe({
            next: (state) => {
                this.configState = state;
                this.correlationWindowSeconds = state.correlationWindowSeconds;
                this.captureSavedSnapshot();
                this.loadingConfig = false;
            },
            error: () => {
                this.loadingConfig = false;
                this.configState = null;
                this.toastr.error('Unable to load camera analytics configuration.', 'Camera Analytics');
            },
        });
    }

    onAnalyticsToggle(definitionId: number, enabled: boolean): void {
        if (!this.configState || this.analyticsReadOnly) {
            return;
        }
        this.configState = {
            ...this.configState,
            rows: this.viewService.toggleRow(this.configState.rows, definitionId, enabled),
        };
    }

    onCorrelationChange(): void {
        // Enables unsaved-change detection for correlation window edits.
    }

    clearConfiguration(): void {
        if (!this.configState || !this.canClearConfiguration) {
            return;
        }
        this.saving = true;
        this.viewService.clearConfiguration(
            this.configState.cameraId,
            this.correlationWindowSeconds,
        ).subscribe({
            next: (state) => {
                this.configState = state;
                this.correlationWindowSeconds = state.correlationWindowSeconds;
                this.captureSavedSnapshot();
                this.saving = false;
                this.toastr.success(
                    'Camera analytics configuration cleared. This camera has no enabled analytics.',
                    'Camera Analytics',
                );
            },
            error: (error) => {
                this.saving = false;
                this.toastr.error(
                    this.extractApiErrorDetail(error)
                    || 'Unable to clear camera analytics configuration.',
                    'Camera Analytics',
                );
            },
        });
    }

    saveConfiguration(): void {
        if (!this.configState || !this.canEdit || !this.eenOauthConfigured) {
            return;
        }

        this.saving = true;
        this.viewService.saveConfiguration(
            this.configState.cameraId,
            this.configState.rows,
            this.correlationWindowSeconds,
            this.configState.configurationSource,
        ).subscribe({
            next: (state) => {
                this.configState = state;
                this.correlationWindowSeconds = state.correlationWindowSeconds;
                this.captureSavedSnapshot();
                this.saving = false;
                const provisioning = state.subscriptionProvisioning?.status;
                const siblingCount = state.subscriptionProvisioning?.sibling_failures?.length || 0;
                if (provisioning === 'unknown') {
                    this.toastr.warning(
                        'Save did not confirm Eagle Eye provisioning. Outcome is unknown — '
                        + 'analytics is not confirmed operational.',
                        'Camera Analytics',
                    );
                } else if (provisioning === 'cleanup_required') {
                    this.toastr.warning(
                        'Eagle Eye replacement subscription is confirmed, but a prior remote '
                        + 'subscription still needs cleanup. Retry save or contact support.',
                        'Camera Analytics',
                    );
                } else if (provisioning === 'provisioned') {
                    this.toastr.success(
                        siblingCount
                            ? 'Camera analytics saved and this camera’s Eagle Eye subscription is provisioned. '
                              + 'A sibling camera reported a separate sync issue.'
                            : 'Camera analytics saved and Eagle Eye subscriptions are provisioned.',
                        'Camera Analytics',
                    );
                } else if (provisioning === 'missing') {
                    this.toastr.warning(
                        'Camera analytics configuration was saved, but a required Eagle Eye '
                        + 'subscription is still missing. Check subscription status below.',
                        'Camera Analytics',
                    );
                } else {
                    this.toastr.success('Camera analytics configuration saved.', 'Camera Analytics');
                }
            },
            error: (error) => {
                this.saving = false;
                const errorCode = this.extractApiErrorCode(error);
                const detail = this.extractApiErrorDetail(error);
                if (errorCode === 'camera_analytics_compensation_failed') {
                    this.toastr.error(
                        detail
                        || 'Provisioning failed and the previous configuration could not be restored. '
                          + 'Local analytics may still be enabled — reload and review before retrying.',
                        'Camera Analytics',
                    );
                } else {
                    this.toastr.error(
                        detail || 'Unable to save camera analytics configuration.',
                        'Camera Analytics',
                    );
                }
                // Reload server state so a failed enablement cannot leave checkboxes
                // optimistically enabled after compensating CAC rollback.
                this.loadConfiguration();
            },
        });
    }

    private extractApiErrorDetail(error: unknown): string | null {
        const detail = (error as { error?: { detail?: unknown } })?.error?.detail;
        return typeof detail === 'string' && detail.trim() ? detail : null;
    }

    private extractApiErrorCode(error: unknown): string | null {
        const code = (error as { error?: { error_code?: unknown } })?.error?.error_code;
        return typeof code === 'string' && code.trim() ? code : null;
    }

    private captureSavedSnapshot(): void {
        this.savedSnapshot = this.serializeState();
    }

    private serializeState(): string {
        if (!this.configState) {
            return '';
        }
        return JSON.stringify({
            correlationWindowSeconds: this.correlationWindowSeconds,
            rows: this.configState.rows.map((row) => ({
                id: row.definition.id,
                enabled: row.isEnabled,
            })),
        });
    }
}
